import SearchInput from "@dashboard/components/AppLayout/ListFilters/components/SearchInput";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { DateTime } from "@dashboard/components/Date/DateTime";
import { ListPageLayout } from "@dashboard/components/Layouts/List/Root";
import { ResponsiveTable } from "@dashboard/components/ResponsiveTable/ResponsiveTable";
import { TablePaginationWithContext } from "@dashboard/components/TablePagination/TablePaginationWithContext";
import TableRowLink from "@dashboard/components/TableRowLink/TableRowLink";
import {
  AtticActivityActorKindEnum,
  type AtticActivityEntryFragment,
  AtticActivityKindEnum,
} from "@dashboard/graphql";
import { sectionNames } from "@dashboard/intl";
import { renderCollection } from "@dashboard/misc";
import { MONO_FONT_FAMILY } from "@dashboard/styles/monoFontFamily";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Box, Button, Input, Select, Skeleton, Text } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";

import { activityLogMessages } from "../../messages";
import { type AtticActivityListUrlFilters } from "../../urls";

const numberOfColumns = 4;

/** The kind select's first option: no kind filter. */
export const KIND_ALL = "";

export interface AtticActivityListPageProps {
  /** undefined while loading. */
  entries: AtticActivityEntryFragment[] | undefined;
  loadFailed: boolean;
  filters: AtticActivityListUrlFilters;
  onSearchChange: (query: string) => void;
  onKindChange: (kind: AtticActivityKindEnum | undefined) => void;
  onCreatedFromChange: (day: string | undefined) => void;
  onCreatedToChange: (day: string | undefined) => void;
  onSubjectChange: (subject: string | undefined) => void;
}

const kindLabels = {
  [AtticActivityKindEnum.CREATED]: activityLogMessages.kindCreated,
  [AtticActivityKindEnum.EDITED]: activityLogMessages.kindEdited,
  [AtticActivityKindEnum.DELETED]: activityLogMessages.kindDeleted,
  [AtticActivityKindEnum.STATUS_CHANGED]: activityLogMessages.kindStatusChanged,
} as const;

const Actor = ({ entry }: { entry: AtticActivityEntryFragment }) => {
  const intl = useIntl();

  switch (entry.actorKind) {
    case AtticActivityActorKindEnum.USER:
      return <Text size={3}>{entry.actorName}</Text>;
    case AtticActivityActorKindEnum.APP:
      return (
        <Box display="flex" alignItems="center" gap={2}>
          <Text size={3}>{entry.actorName}</Text>
          <Box
            as="span"
            paddingX={1}
            borderRadius={2}
            borderWidth={1}
            borderStyle="solid"
            borderColor="default1"
            data-test-id="attic-activity-app-tag"
          >
            <Text size={1} color="default2">
              {intl.formatMessage(activityLogMessages.appTag)}
            </Text>
          </Box>
        </Box>
      );
    default:
      return (
        <Text size={3} color="default2">
          {intl.formatMessage(activityLogMessages.system)}
        </Text>
      );
  }
};

export const AtticActivityListPage = ({
  entries,
  loadFailed,
  filters,
  onSearchChange,
  onKindChange,
  onCreatedFromChange,
  onCreatedToChange,
  onSubjectChange,
}: AtticActivityListPageProps) => {
  const intl = useIntl();
  const kindOptions = [
    { label: intl.formatMessage(activityLogMessages.kindAll), value: KIND_ALL },
    ...Object.values(AtticActivityKindEnum).map(kind => ({
      label: intl.formatMessage(kindLabels[kind]),
      value: kind,
    })),
  ];
  const kindValue = kindOptions.find(option => option.value === (filters.kind ?? KIND_ALL));
  const filtered = !!(
    filters.query ||
    filters.kind ||
    filters.subject ||
    filters.createdFrom ||
    filters.createdTo
  );

  return (
    <ListPageLayout>
      <TopNav
        title={intl.formatMessage(sectionNames.atticActivityLog)}
        subtitle={
          <Text size={2} color="default2">
            {intl.formatMessage(activityLogMessages.subtitle)}
          </Text>
        }
      />
      <DashboardCard data-test-id="attic-activity-list">
        <DashboardCard.Content paddingX={0}>
          <Box
            display="flex"
            alignItems="flex-end"
            gap={4}
            flexWrap="wrap"
            paddingX={6}
            paddingTop={4}
            paddingBottom={2}
          >
            <Box flexGrow="1" __minWidth="240px">
              <SearchInput
                initialSearch={filters.query ?? ""}
                placeholder={intl.formatMessage(activityLogMessages.searchPlaceholder)}
                onSearchChange={onSearchChange}
              />
            </Box>
            <Select
              size="small"
              __width={180}
              label={intl.formatMessage(activityLogMessages.kind)}
              options={kindOptions}
              value={kindValue ?? kindOptions[0]}
              onChange={option =>
                onKindChange((option?.value || undefined) as AtticActivityKindEnum | undefined)
              }
              data-test-id="attic-activity-kind"
            />
            <Input
              size="small"
              type="date"
              label={intl.formatMessage(activityLogMessages.from)}
              value={filters.createdFrom ?? ""}
              onChange={event => onCreatedFromChange(event.target.value || undefined)}
              data-test-id="attic-activity-created-from"
            />
            <Input
              size="small"
              type="date"
              label={intl.formatMessage(activityLogMessages.to)}
              value={filters.createdTo ?? ""}
              onChange={event => onCreatedToChange(event.target.value || undefined)}
              data-test-id="attic-activity-created-to"
            />
          </Box>
          {filters.subject && (
            <Box paddingX={6} paddingBottom={2} display="flex" alignItems="center" gap={3}>
              <Box
                display="inline-flex"
                alignItems="center"
                gap={2}
                paddingX={3}
                paddingY={1}
                borderRadius={4}
                backgroundColor="default2"
                data-test-id="attic-activity-subject-chip"
              >
                <Text size={2}>{intl.formatMessage(activityLogMessages.oneRecord)}</Text>
                <Text size={2} style={{ fontFamily: MONO_FONT_FAMILY }}>
                  {filters.subject}
                </Text>
                <Button
                  variant="tertiary"
                  size="small"
                  onClick={() => onSubjectChange(undefined)}
                  data-test-id="attic-activity-subject-clear"
                >
                  {intl.formatMessage(activityLogMessages.showAll)}
                </Button>
              </Box>
            </Box>
          )}
          <ResponsiveTable footer={<TablePaginationWithContext component="div" />}>
            <TableHead>
              <TableRowLink>
                <TableCell>{intl.formatMessage(activityLogMessages.when)}</TableCell>
                <TableCell>{intl.formatMessage(activityLogMessages.who)}</TableCell>
                <TableCell style={{ width: "100%" }}>
                  {intl.formatMessage(activityLogMessages.what)}
                </TableCell>
                <TableCell>{intl.formatMessage(activityLogMessages.subject)}</TableCell>
              </TableRowLink>
            </TableHead>
            <TableBody>
              {loadFailed ? (
                <TableRowLink data-test-id="attic-activity-list-failed">
                  <TableCell colSpan={numberOfColumns}>
                    <Text size={3} color="critical1">
                      {intl.formatMessage(activityLogMessages.loadFailed)}
                    </Text>
                  </TableCell>
                </TableRowLink>
              ) : (
                renderCollection(
                  entries,
                  row => (
                    <TableRowLink key={row ? row.id : "skeleton"} data-test-id="attic-activity-row">
                      <TableCell style={{ whiteSpace: "nowrap" }}>
                        {row ? (
                          <Text size={3} color="default2">
                            <DateTime date={row.createdAt} plain />
                          </Text>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell style={{ whiteSpace: "nowrap" }}>
                        {row ? <Actor entry={row} /> : <Skeleton />}
                      </TableCell>
                      <TableCell>
                        {row ? (
                          <Text size={3} display="block" __whiteSpace="normal">
                            {row.act}
                          </Text>
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                      <TableCell style={{ whiteSpace: "nowrap" }}>
                        {row ? (
                          row.subject && (
                            <Button
                              variant="tertiary"
                              size="small"
                              onClick={() => onSubjectChange(row.subject)}
                              aria-label={intl.formatMessage(activityLogMessages.filterBySubject, {
                                subject: row.subject,
                              })}
                              data-test-id="attic-activity-subject"
                            >
                              <Text
                                size={2}
                                color="default2"
                                style={{ fontFamily: MONO_FONT_FAMILY }}
                              >
                                {row.subject}
                              </Text>
                            </Button>
                          )
                        ) : (
                          <Skeleton />
                        )}
                      </TableCell>
                    </TableRowLink>
                  ),
                  () => (
                    <TableRowLink data-test-id="attic-activity-list-empty">
                      <TableCell colSpan={numberOfColumns}>
                        <Text size={3} color="default2">
                          {intl.formatMessage(
                            filtered ? activityLogMessages.noMatches : activityLogMessages.empty,
                          )}
                        </Text>
                      </TableCell>
                    </TableRowLink>
                  ),
                )
              )}
            </TableBody>
          </ResponsiveTable>
        </DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};

export default AtticActivityListPage;
